// Dashboard - Article List and Reader Pane
import { useEffect, useState, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useOutletContext } from 'react-router-dom';
import { api, type Feed } from '@/lib/api';
import { formatRelativeTime } from '@/lib/utils';
import { ResizablePane } from '@/components/ResizablePane';
import ArticleList from './components/ArticleList';
import ArticleReader from './components/ArticleReader';
import { AppLayoutContext as AppLayoutContextProvider, type AppLayoutContext } from './AppLayout';

export default function Dashboard() {
  const [selectedArticleId, setSelectedArticleId] = useState<number | null>(null);
  
  // Try React Context first, fallback to Outlet context
  const contextFromProvider = useContext(AppLayoutContextProvider);
  const contextFromOutlet = useOutletContext<AppLayoutContext>();
  
  // Get immediate values from getters if available, otherwise use state
  const getCurrentFeedId = () => {
    const ctx = contextFromProvider || contextFromOutlet;
    return (ctx as any)?.getSelectedFeedId?.() ?? ctx?.selectedFeedId ?? null;
  };
  const getCurrentView = () => {
    const ctx = contextFromProvider || contextFromOutlet;
    return (ctx as any)?.getSelectedView?.() ?? ctx?.selectedView ?? 'all';
  };
  const getCurrentTagId = () => {
    const ctx = contextFromProvider || contextFromOutlet;
    return (ctx as any)?.getSelectedTagId?.() ?? ctx?.selectedTagId ?? null;
  };

  // Always use getters to get the latest values
  const selectedFeedId = getCurrentFeedId();
  const selectedView = getCurrentView();
  const selectedTagId = getCurrentTagId();
  
  console.log('Dashboard context check:', {
    hasProviderContext: !!contextFromProvider,
    providerSelectedFeedId: contextFromProvider?.selectedFeedId,
    providerGetterFeedId: (contextFromProvider as any)?.getSelectedFeedId?.(),
    outletSelectedFeedId: contextFromOutlet?.selectedFeedId,
    usingContext: contextFromProvider ? 'Provider' : 'Outlet',
    finalSelectedFeedId: selectedFeedId,
    finalSelectedView: selectedView,
    finalSelectedTagId: selectedTagId
  });
  
  const queryClient = useQueryClient();

  // Debug logging
  useEffect(() => {
    console.log('Dashboard state:', { selectedView, selectedFeedId, selectedTagId });
  }, [selectedView, selectedFeedId, selectedTagId]);


  // Use getters for query key to ensure it updates immediately
  const currentFeedId = selectedFeedId;
  const currentView = selectedView;
  const currentTagId = selectedTagId;

  const { data: articlesData, isLoading } = useQuery({
    queryKey: ['articles', currentView, currentFeedId ?? 'none', currentTagId ?? 'none'],
    queryFn: async () => {
      // Re-read at query execution time to get absolute latest
      const feedId = getCurrentFeedId();
      const view = getCurrentView();
      const tagId = getCurrentTagId();
      
      const params: any = {
        limit: 100,
        feed_id: feedId ?? undefined,
        tag_id: tagId ?? undefined,
      };
      
      // Set unread filter based on view
      if (view === 'unread') {
        params.unread = true;
      } else if (view === 'read') {
        params.unread = false;
      }
      
      // Set starred filter
      if (view === 'starred') {
        params.starred = true;
      }
      
      console.log('Fetching articles - view:', view, 'feedId:', feedId, 'tagId:', tagId, 'params:', params);
      return api.getArticles(params);
    },
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    enabled: true,
    staleTime: 0,
  });

  const articles = articlesData?.articles || [];
  // API already filters by feed_id, so no need for redundant client-side filtering
  const selectedArticle = selectedArticleId
    ? articles.find((a) => a.id === selectedArticleId)
    : null;

  // Reset article selection when feed/view filter changes
  useEffect(() => {
    setSelectedArticleId(null);
  }, [selectedView, selectedFeedId, selectedTagId]);

  // Invalidate and refetch articles when view/feed/tag selection changes
  useEffect(() => {
    const feedId = getCurrentFeedId();
    const view = getCurrentView();
    const tagId = getCurrentTagId();
    console.log('Selection changed, invalidating articles query.', {
      view,
      feedId,
      tagId,
      selectedView,
      selectedFeedId,
      selectedTagId
    });
    queryClient.invalidateQueries({ queryKey: ['articles'] });
  }, [selectedView, selectedFeedId, selectedTagId, queryClient, contextFromProvider, contextFromOutlet]);

  // Derive labels for the list header with feed metadata
  const feedsData = queryClient.getQueryData<{ feeds: Feed[] }>(['feeds']);
  const activeFeed = feedsData?.feeds.find((f) => f.id === selectedFeedId);
  const activeFeedTitle = activeFeed?.title;
  
  const listTitle = activeFeedTitle
    ? activeFeedTitle
    : selectedTagId
      ? 'Tagged Articles'
      : selectedView === 'unread'
        ? 'Unread'
        : selectedView === 'read'
          ? 'Read'
          : selectedView === 'starred'
            ? 'Starred'
            : 'All Articles';
  
  // Enhanced subtitle with feed metadata
  const getSubtitle = () => {
    if (activeFeed) {
      const parts = ['Showing articles from this feed'];
      if (activeFeed.status === 'error' && activeFeed.last_error) {
        parts.push(`• Error: ${activeFeed.last_error}`);
      } else if (activeFeed.last_fetched_at) {
        const timeAgo = formatRelativeTime(activeFeed.last_fetched_at);
        parts.push(`• Last fetched ${timeAgo}`);
      }
      return parts.join(' ');
    }
    if (selectedTagId) return 'Articles with selected tag';
    if (selectedView === 'unread') return 'Only unread items';
    if (selectedView === 'read') return 'Only read items';
    if (selectedView === 'starred') return 'Starred items';
    return 'All items';
  };
  
  const listSubtitle = getSubtitle();

  return (
    <div className="flex h-full">
      {/* Article List Pane */}
      <ResizablePane
        defaultWidth={384}
        minWidth={250}
        maxWidth={800}
        storageKey="fluxfeed-article-list-width"
        className="border-r border-border"
      >
        <ArticleList
          articles={articles}
          isLoading={isLoading}
          selectedId={selectedArticleId}
          onSelectArticle={setSelectedArticleId}
          title={listTitle}
          subtitle={listSubtitle}
        />
      </ResizablePane>

      {/* Reader Pane */}
      <div className="flex-1 min-w-0">
        <ArticleReader article={selectedArticle} />
      </div>
    </div>
  );
}
